import type { ReactNode } from 'react';
import { forwardRef, useImperativeHandle, useState } from 'react';
import type { CReactNode, SimpleFunc, SimplePromise } from '@bluevision/tools';
import type { FormInstance } from 'antd';
import { Button, Form, Modal } from 'antd';
import type { FormProps } from 'antd/es/form';
import { formLayout, useRequest } from '@bluevision/tools';

import styles from './index.less';

export type ICEditModalFormProps = {
  onSuccess?: SimpleFunc;
  onError?: SimpleFunc;
  onRes?: SimpleFunc;
  btnText?: CReactNode;
  service: SimplePromise;
  onClose?: SimpleFunc;
  formProps?: FormProps;
  getResponse?: boolean;
  disabled?: boolean;
  width?: number;
  title?: CReactNode;
  footer?: CReactNode;

  beforeSubmit?: SimplePromise;
  children?: CReactNode;
  /** 提交按钮文字 */
  submitBtnText?: ReactNode;
};

const { layout, tailLayout } = formLayout;

export interface CEditModalFormRef {
  form: FormInstance;
  onClose?: (success: boolean) => void;
}

// 自定义footer按钮，提交成功不会触发onClose导致Modal无法关闭，所以需要手动调用onClose
const CEditModalForm = forwardRef<CEditModalFormRef, ICEditModalFormProps>((props, ref) => {
  const {
    children,
    disabled,
    onSuccess,
    onError,
    btnText,
    service,
    onClose,
    getResponse,
    formProps,
    width = 480,
    title,
    onRes,
    beforeSubmit,
    footer = null,
    submitBtnText,
  } = props;

  const [form] = Form.useForm();
  useImperativeHandle(ref, () => ({
    form,
    onClose: onClose ?? (() => {}),
  }));

  const [show, setShow] = useState(true);
  const [success, setSuccess] = useState(null);
  const { loading, request } = useRequest(
    service,
    (res) => {
      setSuccess(res || true);
      setShow(false);
      onRes?.(res);
    },
    (error) => {
      onError?.(error);
    },
  );
  const [checkLoading, setCheckLoading] = useState(false);

  // handle history code
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onFinish = (values: any) => {
    if (beforeSubmit) {
      // 自定义检验 loading，兼容之前逻辑
      setCheckLoading(true);

      beforeSubmit?.(values, request)
        .then(() => {
          request(values);
        })
        .catch((e) => e)
        .finally(() => {
          setCheckLoading(false);
        });
    } else {
      request(values);
    }
  };

  const afterClose = () => {
    if (success) {
      onSuccess?.(getResponse ? success : undefined);
    }
    onClose?.(success);
  };

  return (
    <Modal
      footer={footer}
      onCancel={() => setShow(false)}
      afterClose={afterClose}
      open={show}
      width={width}
      maskClosable={false}
      destroyOnClose
      title={title ?? btnText}
      bodyStyle={{ padding: '1px 0' }}
    >
      <div style={{ width: width - 60, margin: '24px auto' }}>
        <Form
          name='c-edit-model'
          {...layout}
          onFinish={onFinish}
          autoComplete='off'
          preserve={false}
          form={form}
          {...formProps}
        >
          {children}
          {!footer ? (
            <Form.Item {...tailLayout}>
              <Button
                loading={loading || checkLoading}
                className={styles.btnWidth}
                type='primary'
                htmlType='submit'
                disabled={disabled}
              >
                <span>{submitBtnText ?? btnText ?? title}</span>
              </Button>
            </Form.Item>
          ) : null}
        </Form>
      </div>
    </Modal>
  );
});
export default CEditModalForm;
